import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pause, Play, Search, User } from 'lucide-react';

// Wspólne pickery produkcji reklam wideo: lista głosów z odsłuchem (▶) i
// galeria avatarów z miniaturami. Używane w VideoAssetsPanel (modal produkcji),
// VideoStrategyPickerModal (tryb AUTO) i IterateVariantModal.

export interface ProductionVoiceOption {
  voiceId?: string;
  name: string;
  description?: string;
  language?: string;
  previewUrl?: string | null;
}

export interface ProductionAvatarOption {
  avatarId?: string;
  name: string;
  previewUrl?: string | null;
  library?: boolean; // avatar/scena z biblioteki tenanta (LabAvatar), nie stock HeyGen
}

function matchesQuery(query: string, ...fields: Array<string | undefined | null>) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return fields.some(field => (field || '').toLowerCase().includes(needle));
}

function SearchInput({ id, value, onChange, placeholder }: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <Search className="w-3.5 h-3.5 text-text-disabled absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        id={id}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs bg-surface-hover border border-border rounded-lg pl-8 pr-3 py-1.5 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
      />
    </div>
  );
}

// ============================================
// Głosy: lista z wyszukiwarką i odsłuchem preview
// ============================================

interface VoicePickerProps {
  idPrefix: string;
  voices: ProductionVoiceOption[];
  value: string;
  onChange: (voiceId: string) => void;
}

export function VoicePicker({ idPrefix, voices, value, onChange }: VoicePickerProps) {
  const [query, setQuery] = useState('');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => () => {
    audioRef.current?.pause();
    audioRef.current = null;
  }, []);

  const filtered = useMemo(
    () => voices.filter(voice => matchesQuery(query, voice.name, voice.description, voice.language)),
    [voices, query],
  );

  const togglePreview = (voice: ProductionVoiceOption) => {
    if (!voice.previewUrl || !voice.voiceId) return;
    if (playingId === voice.voiceId) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(voice.previewUrl);
    audio.onended = () => setPlayingId(current => (current === voice.voiceId ? null : current));
    audio.onerror = () => setPlayingId(current => (current === voice.voiceId ? null : current));
    audioRef.current = audio;
    setPlayingId(voice.voiceId);
    void audio.play().catch(() => setPlayingId(null));
  };

  return (
    <div className="space-y-2">
      <SearchInput
        id={`${idPrefix}-voice-search`}
        value={query}
        onChange={setQuery}
        placeholder={`Szukaj głosu (${voices.length})...`}
      />
      <div className="max-h-52 overflow-y-auto rounded-lg border border-border divide-y divide-border-subtle bg-surface-hover/30">
        {filtered.length === 0 ? (
          <p className="text-[11px] text-text-disabled px-3 py-3">Brak głosów pasujących do wyszukiwania.</p>
        ) : filtered.map(voice => {
          const isSelected = voice.voiceId === value;
          const isPlaying = playingId === voice.voiceId;
          const meta = voice.description || voice.language || '';
          return (
            <div
              key={voice.voiceId}
              className={`flex items-center gap-2 px-2.5 py-1.5 transition-colors ${
                isSelected ? 'bg-accent-primary/10' : 'hover:bg-surface-hover/60'
              }`}
            >
              <button
                type="button"
                onClick={() => voice.voiceId && onChange(voice.voiceId)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
              >
                <span className={`w-3.5 shrink-0 ${isSelected ? 'text-accent-primary' : 'text-transparent'}`}>
                  <Check className="w-3.5 h-3.5" />
                </span>
                <span className="min-w-0">
                  <span className={`text-xs font-medium block truncate ${isSelected ? 'text-accent-primary' : 'text-text-primary'}`}>
                    {voice.name}
                  </span>
                  {meta && <span className="text-[10px] text-text-muted block truncate">{meta}</span>}
                </span>
              </button>
              {voice.previewUrl && (
                <button
                  type="button"
                  onClick={() => togglePreview(voice)}
                  title={isPlaying ? 'Zatrzymaj odsłuch' : 'Odsłuchaj próbkę'}
                  aria-label={isPlaying ? `Zatrzymaj próbkę głosu ${voice.name}` : `Odsłuchaj próbkę głosu ${voice.name}`}
                  className={`p-1.5 rounded-lg border shrink-0 transition-all ${
                    isPlaying
                      ? 'text-accent-primary bg-accent-primary/10 border-accent-primary/30'
                      : 'text-text-muted bg-surface-raised border-border-subtle hover:text-accent-primary hover:border-accent-primary/30'
                  }`}
                >
                  {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// Avatary: galeria miniatur z wyszukiwarką
// ============================================

interface AvatarPickerProps {
  idPrefix: string;
  avatars: ProductionAvatarOption[];
  value: string;
  onChange: (avatarId: string) => void;
}

export function AvatarPicker({ idPrefix, avatars, value, onChange }: AvatarPickerProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(
    () => avatars.filter(avatar => matchesQuery(query, avatar.name)),
    [avatars, query],
  );

  return (
    <div className="space-y-2">
      <SearchInput
        id={`${idPrefix}-avatar-search`}
        value={query}
        onChange={setQuery}
        placeholder={`Szukaj avatara (${avatars.length})...`}
      />
      <div className="max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-hover/30 p-2">
        {filtered.length === 0 ? (
          <p className="text-[11px] text-text-disabled px-1 py-2">Brak avatarów pasujących do wyszukiwania.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {filtered.map(avatar => {
              const isSelected = avatar.avatarId === value;
              return (
                <button
                  key={avatar.avatarId}
                  type="button"
                  onClick={() => avatar.avatarId && onChange(avatar.avatarId)}
                  title={avatar.name}
                  className={`rounded-lg border overflow-hidden text-left transition-all ${
                    isSelected
                      ? 'border-accent-primary ring-1 ring-accent-primary/40'
                      : 'border-border-subtle hover:border-accent-primary/40'
                  }`}
                >
                  <div className="relative aspect-square bg-surface-raised">
                    {avatar.previewUrl ? (
                      <img
                        src={avatar.previewUrl}
                        alt={avatar.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-6 h-6 text-text-disabled" />
                      </div>
                    )}
                    {isSelected && (
                      <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-accent-primary flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    )}
                    {avatar.library && (
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-semibold uppercase tracking-wide bg-accent-primary/80 text-white">
                        Biblioteka
                      </span>
                    )}
                  </div>
                  <span className={`block text-[10px] px-1.5 py-1 truncate ${isSelected ? 'text-accent-primary font-medium' : 'text-text-secondary'}`}>
                    {avatar.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
